import environment from "flow/configuration/environment";
import dateHelper from "flow/utils/dateHelper";

export function validatePersonId(personId: string): boolean {
    if (!personId) {
        return false;
    }

    const formattedPersonId = stripTransform(personId);

    if (formattedPersonId == "Illegal format") {
        return false;
    }

    // First 9 characters must be digits
    // Followed by two control digits or TF
    if (!/^\d{9}(\d{2}|TF)$/.test(formattedPersonId)) {
        return false;
    }

    // No date sanity check for FH numbers
    if (isFHNumber(formattedPersonId)) {
        return verifyCheckDigit(formattedPersonId);
    }

    // Can't be both D and H number at the same time
    if (isDNumber(formattedPersonId) && isHNumber(formattedPersonId)) {
        return false;
    }

    if (!isValidDate(getDate(formattedPersonId))) {
        return false;
    }

    // If it ends with TF we don't verify the checksum
    if (formattedPersonId.endsWith("TF")) {
        return true;
    }

    return verifyCheckDigit(formattedPersonId);
}

function stripTransform(personId: string): string {
    if (personId === undefined || personId === null || personId === "") {
        return "Illegal format";
    }

    //Norwegian culture
    const pId = personId.trim().toUpperCase().replace("-", "").replace(" ", "");
    if (pId.length == 11) return pId;
    //invalid personId
    else return "Illegal format";
}

function doTransform(personId: string): string {
    // Check if personId is undefined or null or empty
    if (personId === undefined || personId === null || personId === "") {
        return "";
    }

    //Norwegian culture
    const pId = personId.trim().toUpperCase().replace("-", "").replace(" ", "");
    if (pId.length == 11) return pId.slice(0, 6) + " " + pId.slice(6);
    //invalid personId but not empty
    else return personId;
}

function isValidDate(dateStr: string): boolean {
    return dateHelper.isValidDateFormat(dateStr);
}

function getDate(personId: string): string {
    let day = parseInt(personId.substring(0, 2));
    let month = parseInt(personId.substring(2, 4));
    let year = parseInt(personId.substring(4, 6));
    const individualNum = parseInt(personId.substring(6, 9), 10);

    // Remove 40 from day if D-number
    if (isDNumber(personId)) {
        day -= 40;
    }

    // Remove 40 from month if H-number
    if (isHNumber(personId)) {
        month -= 40;
    }

    if (!environment.productionMode()) {
        if (isSkatteetatenTest(personId)) {
            month -= 80;
        }

        if (isHelsenettTest(personId)) {
            month -= 65;
        }
    }

    // https://repo.progsbase.com/repoviewer/no.inductive.libraries/foedselsnummer/0.2.3///GetDateFromNorwegianPersonalIdentificationNumber/
    if (individualNum >= 0 && individualNum <= 499) {
        year = year + 1900;
    } else if (individualNum >= 500 && individualNum <= 749 && year >= 54 && year <= 99) {
        year = year + 1800;
    } else if (individualNum >= 900 && individualNum <= 999 && year >= 40 && year <= 99) {
        year = year + 1900;
    } else if (individualNum >= 500 && individualNum <= 999 && year >= 0 && year <= 39) {
        year = year + 2000;
    } else {
        return "Illegal date + individual number combo";
    }

    // Construct a date string in the format YYYY-MM-DD
    return formatDate(year, month, day);
}

function formatDate(year: number, month: number, day: number): string {
    return `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
}

// First number is either 8 or 9
function isFHNumber(personId: string): boolean {
    return /^[89]/.test(personId);
}

// 40 is added to month
function isHNumber(personId: string): boolean {
    const num = parseInt(personId.substring(2, 4));
    return !isNaN(num) && num >= 41 && num <= 52;
}

// 40 is added to day
function isDNumber(personId: string): boolean {
    const num = parseInt(personId.substring(0, 2));
    return !isNaN(num) && num >= 41 && num <= 71;
}

function isSkatteetatenTest(personId: string): boolean {
    const num = parseInt(personId.substring(2, 4));
    return !isNaN(num) && num >= 81 && num <= 92;
}

function isHelsenettTest(personId: string): boolean {
    const num = parseInt(personId.substring(2, 4));
    return !isNaN(num) && num >= 66 && num <= 77;
}

function verifyCheckDigit(formattedPersonId: string): boolean {
    const weights1 = [3, 7, 6, 1, 8, 9, 4, 5, 2];
    const weights2 = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

    const digits = formattedPersonId.split("").map(Number);
    const checksum1 = digits.slice(0, 9).reduce((sum, digit, index) => sum + digit * weights1[index], 0);
    const checksum2 = digits.slice(0, 10).reduce((sum, digit, index) => sum + digit * weights2[index], 0);

    const controlDigit1 = (11 - (checksum1 % 11)) % 11;
    const controlDigit2 = (11 - (checksum2 % 11)) % 11;

    return controlDigit1 === digits[9] && controlDigit2 === digits[10];
}

export default {
    validatePersonId: validatePersonId,
    stripTransform: stripTransform,
    doTransform: doTransform
};
